import {
  ACTIONS,
  type FlattenModelField,
  type ModelInstance,
  type PlainObject,
} from "models/Model";
import type React from "react";
import { useEffect } from "react";
import { FeaturesPlaceholder } from "sections/orders";
import { useForceUpdate } from "utils";
import Field from "./Field";

type FieldGroupProps<T extends PlainObject> = {
  model: ModelInstance<T>;
  fields?: string;
  exclude?: string;
  disabled?: string | boolean;
  readonly?: string | boolean;
  className?: string;
  placeholder?: JSX.Element;
};
export default function FieldGroup<T extends PlainObject>({
  model,
  fields: selected = "",
  exclude,
  disabled = false,
  readonly = false,
  className,
}: FieldGroupProps<T>): React.ReactElement {
  const forceUpdate = useForceUpdate();
  const id = Date.now().toString();
  const isListed = (prop: string, list: string) =>
    list.split(" ").some((f) => [prop, prop.split(".")[0]].includes(f));
  const fullfills = (prop: string, attr: string | boolean) =>
    !!attr && typeof attr === "string" ? isListed(prop, attr) : !!attr;

  const fieldsForSection = model.fields.filter(
    ({ prop }) =>
      (!selected || isListed(prop, selected)) && (!exclude || !isListed(prop, exclude)),
  );
  useEffect(() => {
    // trigger UI update upon model validation (to show any error feedback)
    model.onChange(ACTIONS.VALIDATE(selected), forceUpdate, selected);
  }, [model, selected, forceUpdate]);

  if (!fieldsForSection.length) {
    switch (selected) {
      case "features":
        return <FeaturesPlaceholder />;
      default:
        return <></>;
    }
  }

  return (
    <div className={className}>
      {fieldsForSection.map((field, i) => {
        // @ts-expect-error - isSection property may not exist on all field types
        if (field.isSection) {
          return (
            <section key={i}>
              <h3>{field.label}</h3>
              <FieldGroup
                {...{
                  model,
                  fields: field.prop + ".",
                  exclude,
                  disabled,
                  className,
                }}
              />
            </section>
          );
        }

        const isDisabled = fullfills(field.prop, disabled);
        const isReadonly = fullfills(field.prop, readonly);

        return (
          <Field
            key={i}
            groupId={id}
            model={model}
            field={field as FlattenModelField}
            disabled={isDisabled}
            readonly={isReadonly}
          />
        );
      })}
    </div>
  );
}
